import React from "react";
import {
  addDays,
  addHours,
  areRangesOverlapping,
  differenceInHours,
  differenceInMinutes,
  getMinutes,
  isToday,
  setHours,
  setMinutes,
} from "date-fns";
import {
  __,
  divide,
  find,
  forEachObjIndexed,
  groupBy,
  multiply,
  prop,
  range,
} from "ramda";
import RGL from "react-grid-layout";

import Lines from "images/lines.png";
import styled from "styles/styled_components";
import ellipsis from "styles/ellipsis";

import PersonInfo from "components/schedule/person_info";
import { BORDERS } from "components/schedule/time_block";
import {
  AddShiftButton,
  ShiftButton,
  UnavailButton,
} from "containers/schedule/block_button_containers";

import { filterBlocksByDate, isBlockUnavail } from "helpers/schedule/shifts";
import { getHourLabel, isAllDay } from "helpers/times";
import { IDayPart } from "types/account";
import {
  ISchedule,
  IShift,
  IUnavailabilityBlock,
  Block,
} from "types/schedules";
import { ShiftsTotalHours } from "components/schedule/ShiftsTotalHours";

const ROW_HEIGHT = 68;
const PEOPLE_WIDTH = 15;
const MINUTES_PER_COL = 15;
const MINUTES_PER_HOUR = 60;
const COLS_PER_HOUR = MINUTES_PER_HOUR / MINUTES_PER_COL;

const hoursToColWidth = multiply(COLS_PER_HOUR);
const minutesToColWidth = divide(__, MINUTES_PER_COL);

enum Z_INDEX {
  DAY_PARTS,
  GRID_LINES,
  DAY_PARTS_LABEL,
  BLOCKS,
}

interface IGridDimensions {
  startHour: number;
  startOfDay: Date;
  numberOfHours: number;
  numberOfHourCols: number;
  peopleWidth: number;
  totalCols: number;
}

function getGridDimensions(
  day: Date,
  startHour: number,
  endHour: number
): IGridDimensions {
  let numberOfHours = endHour - startHour;
  if (numberOfHours < 1) {
    // When endHour comes before startHour, it means it should wrap to the next day
    numberOfHours += 24;
  }
  const numberOfHourCols = hoursToColWidth(numberOfHours);
  const totalCols = PEOPLE_WIDTH + numberOfHourCols;
  return {
    startHour,
    startOfDay: setHours(day, startHour),
    numberOfHours,
    numberOfHourCols,
    peopleWidth: PEOPLE_WIDTH,
    totalCols,
  };
}

type RGLElements = [JSX.Element[], RGL.Layout[]];

// Grid Lines
interface IGridLines {
  width: string;
}

const StyledGridLines = styled.div<IGridLines>`
  background-image: url(${Lines});
  background-size: ${(props) => props.width}% ${ROW_HEIGHT}px;
  // This el has a height of 0 in RGL layout, because it shouldn't collide with any other cells.
  // override with !important to make it expand to fill the available height
  top: ${2 * ROW_HEIGHT - 1}px !important;
  bottom: 20px !important;
  height: auto !important;
  z-index: ${Z_INDEX.GRID_LINES};
`;

const StyledDivider = styled.div<{ color: string; height: number }>`
  background-color: ${({ color, theme }) =>
    color === "lighter"
      ? theme.colors.dsGrayLighter
      : theme.colors.dsGrayLight};
  // This el has a height of 0 in RGL layout, because it shouldn't collide with any other cells.
  // override with !important to set line thickness
  height: ${prop("height")}px !important;
  margin-top: -${prop("height")}px;
`;

function makeGridLines({
  numberOfHourCols,
  numberOfHours,
  peopleWidth,
}: IGridDimensions): RGLElements {
  // Calculate percentage width of each grid line, to 3 decimal points
  const gridWidth = (100 / numberOfHours).toFixed(3);
  const gridLines = <StyledGridLines key="gridLines" width={gridWidth} />;
  const layout = {
    i: "gridLines",
    x: peopleWidth,
    y: 1,
    w: numberOfHourCols,
    h: 0,
  };
  return [[gridLines], [layout]];
}

// Hour Labels
const StyledHourWrapper = styled.div`
  border-right: ${({ theme }) => theme.lightBorder};
`;

const StyledHour = styled.div`
  background-color: ${({ theme }) => theme.colors.brandWhite};
  display: inline-block;
  float: right;
  transform: translateX(50%);
`;

function makeHours(
  day: Date,
  { startHour, numberOfHours, peopleWidth }: IGridDimensions
): RGLElements {
  const layout: RGL.Layout[] = [];
  const dayAtMidnight = setHours(day, 0);
  const hours = range(startHour, startHour + numberOfHours + 1);

  const hourLabels = hours.map((hour, idx) => {
    // Include AM/PM suffix at the start and end of the range,
    // or when transitioning between morning/night
    const includeAmPm =
      idx === 0 || idx === hours.length - 1 || hour % 12 === 0;
    const hourKey = `hour-${hour}`;
    layout.push({
      i: hourKey,
      x: peopleWidth + hoursToColWidth(idx - 1),
      y: 0,
      w: 4,
      h: 1,
      static: true,
    });

    return (
      <StyledHourWrapper key={hourKey}>
        <StyledHour>
          {getHourLabel(addHours(dayAtMidnight, hour), includeAmPm)}
        </StyledHour>
      </StyledHourWrapper>
    );
  });

  return [hourLabels, layout];
}

// Current Time marker
interface IStyledTimeMarker {
  minutes: number;
}
const StyledTimeMarker = styled.div<IStyledTimeMarker>`
  &::after {
    background-color: ${({ theme }) => theme.colors.dsOrangeFiery};
    content: "";
    display: block;
    height: ${({ theme }) => theme.indicatorBorder};
    left: ${({ minutes }) => Math.round((100 * minutes) / 60)}%;
    margin-top: -${({ theme }) => theme.indicatorBorder};
    position: relative;
  }
`;

function makeTimeMarker(
  day: Date,
  now: Date,
  { peopleWidth, startOfDay }: IGridDimensions
): RGLElements {
  const layout = [];
  const elements = [];
  if (isToday(day)) {
    const difference = differenceInHours(now, startOfDay);
    layout.push({
      i: "now",
      x: peopleWidth + hoursToColWidth(difference),
      y: 1,
      w: 4,
      h: 0,
      static: true,
    });
    elements.push(<StyledTimeMarker key="now" minutes={getMinutes(now)} />);
  }
  return [elements, layout];
}

const DAY_PART_COLORS = ["#FCF3EB", "#E1F0FA", "#ECF5E1", "#E3EBFE"];
const StyledDayPart = styled.div<{ bg: string }>`
  background-color: ${prop("bg")};
  bottom: 65px !important;
  color: ${(props) => props.theme.colors.dsGrayMedium};
  font-family: ${(props) => props.theme.text.mediumFamily};
  height: auto !important;
  letter-spacing: 2px;
  padding: ${({ theme }) => `calc(5px + ${theme.spacing.small});`}
  text-transform: uppercase;
  top: 0 !important;
  z-index: ${Z_INDEX.DAY_PARTS};
  ${ellipsis}
`;

function makeDayParts(
  dayParts: IDayPart[],
  { numberOfHourCols, peopleWidth, startOfDay, totalCols }: IGridDimensions
): RGLElements {
  const layout: RGL.Layout[] = [];
  const elements: JSX.Element[] = dayParts.reduce(
    (
      acc,
      { startTimeHour, startTimeMinute, endTimeHour, endTimeMinute, name },
      idx
    ) => {
      const startsToday: Date = setHours(
        setMinutes(startOfDay, startTimeMinute),
        startTimeHour
      );
      // endTimeHour can be greater than 23, representing a day part that crosses midnight
      // do mod 24 and then add a day if needed
      let endsToday: Date = setHours(
        setMinutes(startOfDay, endTimeMinute),
        endTimeHour % 24
      );
      if (endTimeHour > 23) {
        endsToday = addDays(endsToday, 1);
      }

      let diffFromStart = differenceInMinutes(startsToday, startOfDay);
      let length = differenceInMinutes(endsToday, startsToday);

      // Correct on LEFT EDGE
      if (diffFromStart < 0) {
        // Reduce the length by the same amount
        length += diffFromStart;
        // Then align this day part to the left edge
        diffFromStart = 0;
      }

      const x = peopleWidth + minutesToColWidth(diffFromStart);
      let w = minutesToColWidth(length);

      // Correct on RIGHT EDGE
      if (x + w > totalCols) {
        // Extends too far right, align to right edge
        w = totalCols - x;
      }

      const key = `part-${name}-${idx}`;
      layout.push({
        i: key,
        x,
        w,
        y: 1,
        h: 1,
        static: true,
      });
      acc.push(
        <StyledDayPart
          key={key}
          bg={DAY_PART_COLORS[idx % DAY_PART_COLORS.length]}
          title={name}
        >
          {name}
        </StyledDayPart>
      );
      return acc;
    },
    [] as JSX.Element[]
  );
  elements.push(
    <StyledDivider key="dayPartsDivider" color="lighter" height={1} />
  );
  layout.push({
    i: "dayPartsDivider",
    x: peopleWidth,
    y: 1,
    w: numberOfHourCols,
    h: 0,
    static: true,
  });
  return [elements, layout];
}

// People and Shifts
const StyledPerson = styled.div`
  border: ${({ theme }) => theme.lightBorder};
  border-left: none;
  border-top: none;
  line-height: ${(props) => props.theme.text.lineHeight.small};
  ${ellipsis}
`;

const StyledBlockWrapper = styled.div`
  cursor: pointer;
  z-index: ${Z_INDEX.BLOCKS};
  & > * {
    margin-left: -1px;
  }
`;

function makePeopleAndBlocks(
  schedule: ISchedule,
  date: Date,
  gridDimensions: IGridDimensions
): RGLElements {
  const layout: RGL.Layout[] = [];
  const { startHour, peopleWidth, totalCols } = gridDimensions;

  // Build the people rows, with the shifts in each row
  let yPosition = 2;
  const peopleAndBlocks = schedule.people.reduce((cells, person) => {
    const todaysShifts = filterBlocksByDate(date, person.shifts) as IShift[];
    const todaysUnavails = person.unavail.match({
      Just: (unavails) => filterBlocksByDate(date, unavails),
      Nothing: () => [],
    }) as IUnavailabilityBlock[];

    // PersonInfo cell
    const isUnassigned = person.assignee.isNothing();
    const personKey = person.assignee.map((a) => a.uuid).unwrapOr("unassigned");
    cells.push(
      <StyledPerson key={personKey}>
        <PersonInfo
          person={person}
          shiftsTotalHours={<ShiftsTotalHours shifts={todaysShifts} />}
        />
      </StyledPerson>
    );
    layout.push({
      i: personKey,
      x: 0,
      y: yPosition,
      w: peopleWidth,
      h: isUnassigned ? todaysShifts.length + 1 : 1,
      static: true,
    });

    // Track block layouts separately so they can be checked for gaps
    const rowBlocksLayout: RGL.Layout[] = [];

    // Shift blocks
    todaysShifts.forEach((shift, shiftIdx) => {
      const shiftKey = `shift-${shift.uuid}`;
      cells.push(
        <StyledBlockWrapper key={shiftKey}>
          <ShiftButton
            border={BORDERS.SHIFT_DAY}
            date={date}
            interactiveView={true}
            shift={shift}
            partialFlag={false}
            person={person}
          />
        </StyledBlockWrapper>
      );

      const [x, w] = getBlockPosition(shift, gridDimensions);
      rowBlocksLayout.push({
        i: shiftKey,
        x,
        // Unassigned row has shifts stacked vertically
        y: isUnassigned ? yPosition + shiftIdx : yPosition,
        w,
        h: 1,
        static: true,
      });
    });

    // Handle extra row height for Unassigned
    if (isUnassigned) {
      // Unassigned row is as tall as its number of shifts
      yPosition += todaysShifts.length;
      // Add the dividing line for the Unassigned row
      cells.push(<StyledDivider key="divider" color="light" height={2} />);
      layout.push({
        i: "divider",
        x: 0,
        y: yPosition + 1,
        w: totalCols,
        h: 0,
        static: true,
      });
    }

    // Unavail blocks
    todaysUnavails.forEach((unavail) => {
      const overlapShift = todaysShifts.find((thisShift) =>
        areRangesOverlapping(
          thisShift.startTime,
          thisShift.endTime,
          unavail.startTime,
          unavail.endTime
        )
      );
      if (overlapShift) {
        // Skip rendering this UnavailButton
        return;
      }
      const unavailKey = `unavail-${unavail.uuid}`;
      cells.push(
        <StyledBlockWrapper key={unavailKey}>
          <UnavailButton
            border={BORDERS.SIDES}
            date={date}
            person={person}
            unavail={unavail}
          />
        </StyledBlockWrapper>
      );

      const [x, w] = getBlockPosition(unavail, gridDimensions);
      rowBlocksLayout.push({
        i: unavailKey,
        x,
        // Unassigned row has shifts stacked vertically
        y: yPosition,
        w,
        h: 1,
        static: true,
      });
    });

    // Group layout by row (y position) to search for gaps
    const layoutByRow = groupBy(
      (l) => prop("y", l).toString(),
      rowBlocksLayout
    );

    // Fill empty rows too
    if (!rowBlocksLayout.length) {
      layoutByRow[yPosition] = [];
    }
    if (isUnassigned) {
      layoutByRow[yPosition] = [];
    }

    // AddShiftButton (fill in gaps)
    forEachObjIndexed((rowLayout, rowYPosition) => {
      const gaps = findGaps(
        rowLayout,
        Number(rowYPosition),
        `add-${personKey}-${rowYPosition}`,
        gridDimensions
      );
      gaps.forEach((gap) => {
        // Get the hour value for this gap, so it can be passed to openBlock
        const gapHour = startHour + (gap.x - peopleWidth) / COLS_PER_HOUR;
        cells.push(
          <StyledBlockWrapper key={gap.i}>
            <AddShiftButton
              border={BORDERS.SIDES}
              date={date}
              defaultHour={gapHour}
              delayHover
              numShifts={1}
              person={person}
            />
          </StyledBlockWrapper>
        );
        layout.push(gap);
      });
    }, layoutByRow);

    // Combine blocks with full layout array
    layout.push(...rowBlocksLayout);

    yPosition++;
    return cells;
  }, [] as JSX.Element[]);
  return [peopleAndBlocks, layout];
}

function getBlockPosition(
  block: Block,
  {
    numberOfHours,
    numberOfHourCols,
    peopleWidth,
    startOfDay,
    totalCols,
  }: IGridDimensions
): [number, number] {
  // Block has a column position based on its start time:
  // - starting at people width
  // - difference of (start time - start of day) (in minutes per col)
  // - with an exception, all-day Unavails just fill the entire space:
  if (isBlockUnavail(block) && isAllDay(block.startTime, block.endTime)) {
    return [peopleWidth, numberOfHourCols];
  }

  let minutesFromStart = differenceInMinutes(block.startTime, startOfDay);
  // Length is length of block in minutes (as minutes per col)
  let blockLengthMinutes = differenceInMinutes(block.endTime, block.startTime);
  // If blockLengthMinutes < 1 that indicates an end time on the following day
  if (blockLengthMinutes < 1) {
    blockLengthMinutes += numberOfHours * 60;
  }

  if (minutesFromStart < 0) {
    // Negatively placed blocks start before the trading day. They should be aligned to left edge.
    // Reduce the length to reflect the cut-off portion
    blockLengthMinutes += minutesFromStart;
    // Align block to left edge
    minutesFromStart = 0;
  }

  const blockColPosition = peopleWidth + minutesToColWidth(minutesFromStart);

  // Block length cannot be less than 1 hour
  if (blockLengthMinutes < MINUTES_PER_HOUR) {
    blockLengthMinutes = MINUTES_PER_HOUR;
  }
  let blockLength = minutesToColWidth(blockLengthMinutes);
  // Block length cannot be larger than total width of grid
  if (blockColPosition + blockLength > totalCols) {
    blockLength = totalCols - blockColPosition;
  }

  return [blockColPosition, blockLength];
}

// Given an array of layout objects on a single row,
// return new layout objects representing the gaps in that row.
const MAX_GAP_SIZE = hoursToColWidth(1); // create shift gaps of 7hrs max
function findGaps(
  layout: RGL.Layout[],
  yPosition: number,
  key: string,
  { peopleWidth, totalCols }: IGridDimensions
): RGL.Layout[] {
  const gaps: RGL.Layout[] = [];
  const skippedLayoutIdxs: string[] = [];
  let keyIdx = 0;

  // Try to find a full width gap
  let x = peopleWidth;
  let w = MAX_GAP_SIZE;
  while (w && x + w <= totalCols) {
    // Check for a collision on the x position
    const xCollision = find((thisLayout: RGL.Layout) => {
      return (
        // Has this layout not already been skipped?
        skippedLayoutIdxs.indexOf(thisLayout.i as string) === -1 &&
        // Is x within the span of thisLayout?
        (isPositionWithinLayout(x, thisLayout) ||
          // Or is x less than an hour's distance from thisLayout
          isPositionWithinLayout(x + COLS_PER_HOUR - 1, thisLayout))
      );
    }, layout);
    if (xCollision) {
      // Skip to the end of this block
      x = xCollision.x + xCollision.w;
      skippedLayoutIdxs.push(xCollision.i as string);

      // Move to nearest hour line
      x =
        hoursToColWidth(Math.ceil((x - peopleWidth) / COLS_PER_HOUR)) +
        peopleWidth;
      if (x + w > totalCols) {
        // We're approaching the end of the row, shrink to minimal width
        w = COLS_PER_HOUR;
      }
    }

    // Check for a collision on the gap width (right edge of gap)
    const rightEdge = x + w;
    const wCollision = find((thisLayout: RGL.Layout) => {
      return (
        // Is right edge within the span of thisLayout?
        isPositionWithinLayout(rightEdge, thisLayout) ||
        // Or is thisLayout fully within the gap?
        (thisLayout.x >= x && thisLayout.x + thisLayout.w <= rightEdge)
      );
    }, layout);
    if (wCollision) {
      // Shrink the gap to not overlap
      w = wCollision.x - x;
    }
    if (x + w <= totalCols && w >= COLS_PER_HOUR) {
      // If dimensions are within bounds of totalCols,
      // and w is at least on hour, we found a gap.
      gaps.push({
        x,
        w,
        y: yPosition,
        i: `${key}-${keyIdx++}`,
        h: 1,
        static: true,
      });
    }
    // Move cursor to end of gap and continue
    x = Math.min(x + w, totalCols); // up to totalCols
    w = Math.min(MAX_GAP_SIZE, totalCols - x); // up to totalCols - x
  }
  return gaps;
}

function isPositionWithinLayout(position: number, layout: RGL.Layout) {
  return position >= layout.x && position <= layout.x + layout.w;
}

export {
  findGaps,
  getBlockPosition,
  getGridDimensions,
  makeDayParts,
  makeGridLines,
  makeHours,
  makePeopleAndBlocks,
  makeTimeMarker,
  RGLElements,
  ROW_HEIGHT,
};
